/**
 * The typed API client. `ARCHITECTURE.md` §API conventions: "Types flow to the SPA
 * through Hono's RPC client (`hc<AppType>`): no OpenAPI, no codegen."
 *
 * Two clients over one fetch, because the API has two doors with two shapes:
 * `client.rest` is the client portal's REST surface (`GET /api/clients/me`), and
 * `client.studio` is the studio's RPC one (`POST /api/studio/<action>`), whose
 * schema is generated from the action union. Better Auth is a third and is not
 * here: it has its own client, and its errors are `{ code, message }` rather than
 * problem+json (`modules/auth/auth.ts`, note 3).
 *
 * `createApiClient(fetchImpl)` is what makes a component test possible without a
 * server: `src/test/fakeApi.ts` builds a `fetch` from a route table and passes it
 * here, so the code under test is the real client and the real hooks.
 */

import { hc } from 'hono/client'

import type { ApiType, StudioType } from '@fareast/api'

import { fetchUpload, xhrUpload, type Uploader } from './upload.js'

/**
 * Where the API hangs, as the browser addresses it.
 *
 * Repeated rather than imported: `apps/web` may take **types only** from
 * `@fareast/api` (`ARCHITECTURE.md` §Repository, lint-enforced), and a path is not
 * a type. The other statements of the same two strings are `apps/api/src/app.ts`
 * `API_PREFIX`, `apps/api/src/actions/http.ts` `STUDIO_PREFIX`, the dev proxy in
 * `vite.config.ts`, and session 22's nginx `location` blocks. Same origin in every
 * one of them, which is why they are relative here — the SPA is served from the
 * origin it calls (`STACK.md` §Auth).
 */
export const API_BASE_PATH = '/api'
export const STUDIO_BASE_PATH = '/api/studio'

export type ApiClient = {
  /** `GET /api/clients/me` → `client.rest.clients.me.$get()`. */
  readonly rest: ReturnType<typeof restClient>
  /** `clients_get` → `client.studio.clients_get.$post({ json })` (session 20). */
  readonly studio: ReturnType<typeof studioClient>
  /** The fetch every call goes through. `useApiMutation` needs it for raw headers. */
  readonly fetch: typeof fetch
  /**
   * Sends one file as a request's body (`api/upload.ts`). In the browser it reports
   * the bytes as they go; given a fetch — a test's fake — it goes through that fetch,
   * so the fake is the whole network.
   */
  readonly upload: Uploader
}

/**
 * `credentials: 'include'`, on every request.
 *
 * `ARCHITECTURE.md` §Security: the session is a `HttpOnly`, `SameSite=Lax`,
 * same-origin cookie and there is no token in `localStorage`. Same-origin fetch
 * sends it by default, so this is belt rather than braces — but the day a
 * deployment puts the SPA on a second hostname, the failure it prevents is every
 * request silently anonymous rather than a visible error.
 */
const INIT: RequestInit = { credentials: 'include' }

export function createApiClient(given?: typeof fetch): ApiClient {
  const fetchImpl = given ?? globalThis.fetch
  /*
   * Bound once, here, rather than left as the bare reference `globalThis.fetch`
   * (or a test double) evaluates to. Its direct caller — `ImpersonateButton` —
   * calls it as `api.fetch(…)`, a method invocation whose receiver is the `ApiClient`
   * object rather than `window`. A real browser's native `fetch` is a legacy platform operation that
   * checks its receiver and throws `TypeError: Failed to execute 'fetch' on
   * 'Window': Illegal invocation` for any receiver that is not the window itself
   * — undefined (a bare call, which is how Hono's own client invokes it below) is
   * tolerated, but a foreign object is not. `.bind(globalThis)` fixes the
   * receiver permanently, so it is correct called either way and under a test
   * double that never inspects `this`.
   *
   * `Function.prototype.bind` returns a fresh function object with none of the
   * original's own properties, so `src/test/fakeApi.ts`'s `.calls`/`.callsTo` —
   * attached to the fake's own function object, and read straight off
   * `apiClient.fetch` by `src/test/fakeApi.ts` `callsOf` — would otherwise vanish
   * behind the bound wrapper. Copying them across keeps the bound function
   * callable *and* introspectable; `calls`' value is the fake's own live array
   * reference; `Object.assign` copies the reference, not a snapshot, so a later
   * `push` inside the fake is still visible through either function.
   */
  const boundFetch = Object.assign(fetchImpl.bind(globalThis), fetchImpl)
  return {
    rest: restClient(boundFetch),
    studio: studioClient(boundFetch),
    fetch: boundFetch,
    upload: given === undefined ? xhrUpload : fetchUpload(boundFetch),
  }
}

function restClient(fetchImpl: typeof fetch) {
  return hc<ApiType>(API_BASE_PATH, { fetch: fetchImpl, init: INIT })
}

function studioClient(fetchImpl: typeof fetch) {
  return hc<StudioType>(STUDIO_BASE_PATH, { fetch: fetchImpl, init: INIT })
}
